import { ReactNode } from "react"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import {
  InformationCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid"
import styles from "./style.module.scss"
type Variant = "info" | "warning" | "success" | "danger"
type Size = "sm" | "default"

interface AlertProps {
  title?: ReactNode
  content: ReactNode
  variant?: Variant
  size?: Size
  injectable?: boolean
}

const variantStyles: Record<
  Variant,
  {
    text: string
    iconColor: string
    bg: string
    hoverBorder: string
  }
> = {
  info: {
    text: "text-system-sea-500",
    iconColor:
      "text-system-sea-500 group-hover:text-basic-white transition-colors duration-200",
    bg: "bg-system-sea-500/30 group-hover:bg-system-sea-500 transition-colors duration-200",
    hoverBorder:
      "hover:border-l-4 border-l-system-blue-800 hover:border-l-system-sea-500 transition-colors duration-200",
  },
  warning: {
    text: "text-system-yellow-500",
    iconColor:
      "text-system-yellow-500 group-hover:text-basic-white transition-colors duration-200",
    bg: "bg-system-yellow-500/30 group-hover:bg-system-yellow-500 transition-colors duration-200",
    hoverBorder:
      "hover:border-l-4 border-l-system-blue-800 hover:border-l-system-yellow-500 transition-colors duration-200",
  },
  success: {
    text: "text-system-success-500",
    iconColor:
      "text-system-success-500 group-hover:text-basic-white transition-colors duration-200",
    bg: "bg-system-success-500/30 group-hover:bg-system-success-500 transition-colors duration-200",
    hoverBorder:
      "hover:border-l-4 border-l-system-blue-800 hover:border-l-system-success-500 transition-colors duration-200",
  },
  danger: {
    text: "text-system-error-500",
    iconColor:
      "text-system-error-500 group-hover:text-basic-white transition-colors duration-200",
    bg: "bg-system-error-500/30 group-hover:bg-system-error-500 transition-colors duration-200",
    hoverBorder:
      "hover:border-l-4 border-l-system-blue-800 hover:border-l-system-error-500 transition-colors duration-200",
  },
}

const variantIcons: Record<Variant, typeof InformationCircleIcon> = {
  info: InformationCircleIcon,
  warning: ExclamationTriangleIcon,
  success: CheckCircleIcon,
  danger: XCircleIcon,
}

const sizeStyles: Record<Size, { icon: string }> = {
  sm: {
    icon: "w-system-sm h-system-sm",
  },
  default: {
    icon: "w-system-lg h-system-lg",
  },
}

export default function Alert({
  title,
  content,
  variant = "info",
  size = "default",
  injectable = true,
}: AlertProps) {
  const Icon = variantIcons[variant]

  return (
    <div
      className={cn(
        "transition-all duration-200 ease-in-out bg-system-blue-800 rounded-system-md p-system-md cursor-pointer",
        "group",
        styles.container,
        // variantStyles[variant].hoverBorder,
      )}
    >
      {size === "sm" ? (
        <div className="flex items-center gap-system-sm text-basic-white">
          <div
            className={cn(sizeStyles.sm.icon, variantStyles[variant].iconColor)}
          >
            <Icon className="w-system-sm h-system-sm" />
          </div>
          {injectable ? (
            <div
              className="text-basic-white/90"
              dangerouslySetInnerHTML={{ __html: String(content || "") }}
            />
          ) : (
            <div className="text-basic-white/90">{content}</div>
          )}
        </div>
      ) : (
        <div className="flex items-start gap-system-sm text-basic-white">
          <div
            className={cn(
              "rounded-system-sm p-system-xs",
              variantStyles[variant].iconColor,
              variantStyles[variant].bg,
            )}
          >
            <Icon className={cn(sizeStyles[size].icon)} />
          </div>
          <div className="flex-1 min-w-0">
            {title && (
              <div className={cn("font-semibold", variantStyles[variant].text)}>
                {title}
              </div>
            )}
            <div className={cn("text-basic-white/90", title && "mt-1")}>
              {injectable ? (
                <div
                  dangerouslySetInnerHTML={{ __html: String(content || "") }}
                />
              ) : (
                content
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
